import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import {
  paymentModels,
  regulationOptions,
} from '@nxsys/constants/AppConstants';
import { useGetWTRList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetWTRListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { formatFullName, getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce, find } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import RTWFilterModal, { RTWFilterValues } from '../components/RTWFilterModal';
import { getWTRColumns } from './columns';

const WTRReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { isAgency, isBureau } = useSelector((state: AppState) => state.appStore);

  const dispatch = useDispatch();
  const { wtrList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = wtrList;
  const currentTaxWeek = getTaxWeekTaxYear().taxWeek;

  const [selectedRows, setSelectedRows] = useState<IWTR[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingWTRList, run: getWTRList } = useGetWTRList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        {!isAgency && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle prefix={messages['common.wtr']} separator="" title="" />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetWTRListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(reportActions.getWTRList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxPeriod: currentTaxWeek,
    });
  }, [currentTaxWeek]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: RTWFilterValues) => {
    const params: GetWTRListParams = {
      ...queryParams,
      companyIds: values.companies?.map((c) => c.value),
      agencyIds: values.agencies?.map((a) => a.value),
      taxYear: values.taxYear,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IWTR[]) => {
    const dataExport = data.map((item) => {
      const {
        agencyRef,
        systemId,
        id,
        primaryAgency,
        totalHour,
        companyPayment,
        setting,
        taxPeriod,
        workingTimeRegulation,
        isBreached,
        secondaryAgency,
      } = item;
      return {
        id,
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${
          agencyRef || systemId || ''
        }`,
        name: formatFullName(item),
        paymentCompany: companyPayment?.companyName || '',
        paymentModel: setting?.paymentModel
          ? paymentModels[setting?.paymentModel as keyof typeof paymentModels]
          : '',
        taxPeriod: taxPeriod,
        totalHours: totalHour || '',
        status: workingTimeRegulation
          ? find(regulationOptions, (o) => o?.value === workingTimeRegulation)
              ?.label
          : '',
        breached:
          workingTimeRegulation !== 'OPT_IN'
            ? 'N/A'
            : isBreached
              ? 'Yes'
              : 'No',
        primaryAgencyName: primaryAgency?.name || '',
        secondaryAgency: secondaryAgency?.name || '',
      };
    });

    const headers = [
      'Employee ID',
      'Agency Reference',
      'Name',
      'Payment Company',
      'Payment Model',
      'Tax Period',
      'No of Hours worked',
      'Status',
      'Breached',
      'Primary Agency',
      'Secondary Agency',
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataExport);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.utils.sheet_add_aoa(worksheet, [headers]);
    XLSX.writeFile(
      workbook,
      `WTR-export-on-${moment(new Date()).format('DD/MM/YYYY')}.xlsx`,
      {
        compression: true,
      },
    );
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getWTRList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingWTRList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            {!isAgency && (
              <div className="flex flex-wrap gap-4 items-end">
                <TaxWeekMonthSelect
                  initValue={currentTaxWeek}
                  onFilter={(value) =>
                    handleFetchData({
                      ...queryParams,
                      taxPeriod: value.taxPeriod,
                    })
                  }
                />
              </div>
            )}
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2700, y: 700 }}
            columns={getWTRColumns({
              messages,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <RTWFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default WTRReport;
